import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApp } from "@/context/AppContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { GOOGLE_MAPS_LOADER, GOOGLE_MAPS_KEY } from "@/lib/maps";
import { CheckCircle2, MapPin, Info } from "lucide-react";

const OTA_OPTS = ["Booking", "Airbnb", "Expedia", "Vrbo", "Google"];

export default function EditPropertyModal({ prop, open, onClose, onSaved }) {
  const { t, lang } = useApp();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ac, setAc] = useState(null);
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER);

  useEffect(() => {
    if (prop) {
      setF({
        name: prop.name || "", address: prop.address || "", rooms: prop.rooms || 1,
        property_type: prop.property_type || "hotel", base_price: prop.base_price ?? 100,
        min_price: prop.min_price ?? 50, max_price: prop.max_price ?? 400,
        pms: prop.pms || "", channel_manager: prop.channel_manager || "", otas: prop.otas || [],
        booking_url: prop.booking_url || "",
        breakfast_cost: prop.breakfast_cost ?? "", breakfast_value: prop.breakfast_value ?? "",
        city_tax_per_person: prop.city_tax_per_person ?? "",
        default_meal_plan: prop.default_meal_plan || "room_only",
        breakfast_calc_mode: prop.breakfast_calc_mode || "per_room",
        event_radius_km: prop.event_radius_km || 30,
        latitude: prop.latitude ?? null, longitude: prop.longitude ?? null,
        formatted_address: prop.formatted_address || "", city: prop.city || "",
        province: prop.province || "", region: prop.region || "", country: prop.country || "",
        postcode: prop.postcode || "", place_id: prop.place_id || "",
      });
    }
  }, [prop]);

  if (!f) return null;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleOta = (o) => set("otas", f.otas.includes(o) ? f.otas.filter((x) => x !== o) : [...f.otas, o]);
  const addressChanged = prop && (f.address || "").trim() !== (prop.address || "").trim();

  const onPlaceChanged = () => {
    if (!ac) return;
    const place = ac.getPlace();
    if (!place || !place.geometry) return;
    const loc = place.geometry.location;
    const comp = {};
    (place.address_components || []).forEach((c) => (c.types || []).forEach((tp) => { comp[tp] = c.long_name; }));
    setF((s) => ({
      ...s,
      address: place.formatted_address || s.address,
      formatted_address: place.formatted_address || "",
      latitude: loc.lat(), longitude: loc.lng(), place_id: place.place_id || "",
      city: comp.locality || comp.administrative_area_level_3 || comp.administrative_area_level_2 || "",
      province: comp.administrative_area_level_2 || "",
      region: comp.administrative_area_level_1 || "",
      country: comp.country || "", postcode: comp.postal_code || "",
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/properties/${prop.id}`, {
        ...f, rooms: Number(f.rooms), base_price: Number(f.base_price),
        min_price: Number(f.min_price), max_price: Number(f.max_price),
        event_radius_km: Number(f.event_radius_km),
        breakfast_cost: f.breakfast_cost === "" ? null : Number(f.breakfast_cost),
        breakfast_value: f.breakfast_value === "" ? null : Number(f.breakfast_value),
        city_tax_per_person: f.city_tax_per_person === "" ? null : Number(f.city_tax_per_person),
      });
      toast.success(lang === "it" ? "Struttura aggiornata" : "Property updated");
      onSaved && (await onSaved(data));
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const geoReady = f.latitude != null && f.longitude != null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-property-modal">
        <DialogHeader><DialogTitle>{t("editTitle")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>{lang === "it" ? "Nome struttura" : "Property name"}</Label>
            <Input data-testid="edit-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1" /></div>

          <div><Label>{t("address")}</Label>
            {isLoaded && GOOGLE_MAPS_KEY ? (
              <Autocomplete onLoad={setAc} onPlaceChanged={onPlaceChanged}
                fields={["address_components", "formatted_address", "geometry", "place_id"]} options={{ types: ["geocode"] }}>
                <Input data-testid="edit-address" value={f.address} onChange={(e) => set("address", e.target.value)} className="mt-1"
                  placeholder={lang === "it" ? "Digita e seleziona dall'elenco Google..." : "Type and pick from Google..."} />
              </Autocomplete>
            ) : (
              <Input data-testid="edit-address" value={f.address} onChange={(e) => set("address", e.target.value)} className="mt-1" />
            )}
            {geoReady && (
              <p data-testid="edit-geo-detected" className="text-xs mt-1.5 flex items-center gap-1.5" style={{ color: "var(--demand-low)" }}>
                <CheckCircle2 className="w-3.5 h-3.5" />
                {[f.city, f.province, f.region, f.country].filter(Boolean).join(", ")}
                <span className="font-data text-muted-foreground">({Number(f.latitude).toFixed(4)}, {Number(f.longitude).toFixed(4)})</span>
              </p>
            )}
            {addressChanged && (
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-1.5 flex items-start gap-1.5">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{t("addressChangeNote")}</p>
            )}
          </div>

          <div><Label>{lang === "it" ? "Link Booking.com (consigliato)" : "Booking.com link (recommended)"}</Label>
            <Input data-testid="edit-booking-url" placeholder="https://www.booking.com/hotel/..." value={f.booking_url} onChange={(e) => set("booking_url", e.target.value)} className="mt-1" />
            <p className="text-xs text-muted-foreground mt-1">{lang === "it" ? "Profilo, servizi e prezzo pubblico reale della tua struttura. Cambiandolo, i dati di mercato verranno rigenerati." : "Your property's profile, amenities and real public price. Changing it regenerates market data."}</p></div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("rooms")}</Label>
              <Input data-testid="edit-rooms" type="number" min="1" value={f.rooms} onChange={(e) => set("rooms", e.target.value)} className="mt-1" /></div>
            <div><Label>{t("type")}</Label>
              <Select value={f.property_type} onValueChange={(v) => set("property_type", v)}>
                <SelectTrigger data-testid="edit-type" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hotel">Hotel</SelectItem>
                  <SelectItem value="bnb">B&B</SelectItem>
                  <SelectItem value="vacation_home">{lang === "it" ? "Casa vacanza" : "Vacation home"}</SelectItem>
                  <SelectItem value="short_rental">{lang === "it" ? "Affitto breve" : "Short rental"}</SelectItem>
                </SelectContent>
              </Select></div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div><Label>{t("basePrice")} €</Label>
              <Input data-testid="edit-base" type="number" value={f.base_price} onChange={(e) => set("base_price", e.target.value)} className="mt-1" /></div>
            <div><Label>{t("minPrice")} €</Label>
              <Input data-testid="edit-min" type="number" value={f.min_price} onChange={(e) => set("min_price", e.target.value)} className="mt-1" /></div>
            <div><Label>{t("maxPrice")} €</Label>
              <Input data-testid="edit-max" type="number" value={f.max_price} onChange={(e) => set("max_price", e.target.value)} className="mt-1" /></div>
          </div>

          <div className="rounded-lg border border-border p-3 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{lang === "it" ? "Trattamento & Colazione (per il pricing)" : "Meal plan & Breakfast (for pricing)"}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{lang === "it" ? "Costo colazione (€)" : "Breakfast cost (€)"}</Label>
                <Input data-testid="edit-bf-cost" type="number" step="0.5" placeholder="4" value={f.breakfast_cost} onChange={(e) => set("breakfast_cost", e.target.value)} className="mt-1" /></div>
              <div><Label>{lang === "it" ? "Valore commerciale colazione (€)" : "Breakfast commercial value (€)"}</Label>
                <Input data-testid="edit-bf-value" type="number" step="0.5" placeholder="8" value={f.breakfast_value} onChange={(e) => set("breakfast_value", e.target.value)} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-1 gap-1">
              <Label>{lang === "it" ? "Tassa di soggiorno (€ per persona a notte)" : "City tax (€ per person per night)"}</Label>
              <Input data-testid="edit-city-tax" type="number" step="0.5" placeholder="5.5" value={f.city_tax_per_person} onChange={(e) => set("city_tax_per_person", e.target.value)} className="mt-1" />
              <p className="text-[11px] text-muted-foreground">{lang === "it" ? "Es. Napoli 5,5€. Il prezzo che Apify legge da Booking è lordo (tassa inclusa): il motore la scorpora (usando 2 persone) prima di applicare la tua % di offerte, così ragiona sul vero netto — coerente con lo storico che è già al netto della tassa. Il prezzo consigliato principale è mostrato al netto della tassa, con il lordo Booking come riferimento." : "E.g. Naples €5.5. The price Apify reads from Booking is gross (tax included): the engine strips it (using 2 guests) before applying your offers %, so it reasons on the true net — consistent with your tax-free history. The main recommended price is shown net of tax, with the Booking gross as reference."}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{lang === "it" ? "Trattamento predefinito" : "Default meal plan"}</Label>
                <select data-testid="edit-default-mp" value={f.default_meal_plan} onChange={(e) => set("default_meal_plan", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-border bg-background px-2 text-sm">
                  <option value="room_only">{lang === "it" ? "Solo pernottamento (senza colazione)" : "Room Only (no breakfast)"}</option>
                  <option value="bed_breakfast">{lang === "it" ? "Con colazione inclusa (B&B)" : "Bed & Breakfast (breakfast included)"}</option>
                </select>
                <p className="mt-1 text-[11px] text-muted-foreground">{lang === "it" ? "Indica se le tue tariffe includono la colazione: rende il prezzo consigliato e il confronto coi competitor più precisi." : "Whether your rates include breakfast: makes the recommended price and competitor comparison more precise."}</p></div>
              <div><Label>{lang === "it" ? "Calcolo colazione" : "Breakfast calc"}</Label>
                <select data-testid="edit-bf-mode" value={f.breakfast_calc_mode} onChange={(e) => set("breakfast_calc_mode", e.target.value)} className="mt-1 w-full h-10 rounded-md border border-border bg-background px-2 text-sm">
                  <option value="per_room">{lang === "it" ? "Per camera a notte" : "Per room / night"}</option>
                  <option value="per_person">{lang === "it" ? "Per persona a notte" : "Per person / night"}</option>
                </select></div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("pms")}</Label>
              <Input data-testid="edit-pms" value={f.pms} onChange={(e) => set("pms", e.target.value)} className="mt-1" /></div>
            <div><Label>{t("channelManager")}</Label>
              <Input data-testid="edit-cm" value={f.channel_manager} onChange={(e) => set("channel_manager", e.target.value)} className="mt-1" /></div>
          </div>

          <div><Label>{t("otas")}</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {OTA_OPTS.map((o) => (
                <button type="button" key={o} data-testid={`edit-ota-${o.toLowerCase()}`} onClick={() => toggleOta(o)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${f.otas.includes(o) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}>{o}</button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="edit-cancel">{t("cancel")}</Button>
          <Button onClick={save} disabled={busy || !f.name || !f.address} data-testid="edit-save">
            {busy ? t("savingChanges") : t("saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
